// ContactUs — CoGrad Premium White Theme
import React, { useState, useEffect } from "react";
import "./ContactUs.scss";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet";
import emailjs from "@emailjs/browser";
import { motion } from "framer-motion";
import { impact } from "../data/stats";
import AsOfNote from "../components/AsOfNote";
import {
  Phone, Mail, MapPin, Send, CheckCircle, Clock,
  GraduationCap, Building2, Briefcase, Newspaper,
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

// ── Animations ────────────────────────────────────────────────────────────────
const FADE_UP = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.52, ease: [0.22, 1, 0.36, 1], delay: i * 0.09 },
  }),
};

// ── Data ──────────────────────────────────────────────────────────────────────
const INTENTS = [
  {
    id: "student",
    label: "Student",
    icon: GraduationCap,
    heading: "Student Inquiry",
    sub: "Interested in Fellowship, Medha AI, or any of our programs?",
    placeholder: "Tell us about yourself and what you're looking to achieve with CoGrad…",
  },
  {
    id: "school",
    label: "School / Institution",
    icon: Building2,
    heading: "Institution Partnership",
    sub: "Want to bring CoGrad programs to your school or district?",
    placeholder: "Tell us about your institution and how you'd like to partner with us…",
  },
  {
    id: "investor",
    label: "Investor / Corporate",
    icon: Briefcase,
    heading: "Partnership & Investment",
    sub: "Interested in collaborating with or investing in CoGrad?",
    placeholder: "Tell us about your organization and your collaboration interest…",
  },
  {
    id: "press",
    label: "Press / Media",
    icon: Newspaper,
    heading: "Press & Media",
    sub: "Writing about CoGrad? We'd love to help you tell the story.",
    placeholder: "Share your publication details and what you're working on…",
  },
];

const INFO_CARDS = [
  {
    icon: Phone,
    label: "Phone",
    value: "+91-9220253001",
    href: "tel:+919220253001",
    sub: "Mon–Sat, 10am – 6pm IST",
  },
  {
    icon: Mail,
    label: "Email",
    value: "connect@cograd.in",
    href: "mailto:connect@cograd.in",
    sub: "We reply within 24 hours",
  },
  {
    icon: MapPin,
    label: "Office",
    value: "C-56A/28, Sector 62, Noida 201301",
    href: "https://share.google/pCSEVWBh8IBZALHtP",
    sub: "PI Softek Ltd",
  },
];

const WA_LINK =
  "https://wa.me/919220253001?text=Hi%20CoGrad%20team%2C%20I%20wanted%20to%20reach%20out...";

// ── WhatsApp SVG ──────────────────────────────────────────────────────────────
const WaIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

// ── Component ─────────────────────────────────────────────────────────────────
const ContactUs = () => {
  useEffect(() => {
    emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
  }, []);

  const [activeIntent, setActiveIntent] = useState("student");
  const [isLoading,    setIsLoading]    = useState(false);
  const [sent,         setSent]         = useState(false);
  const [formData,     setFormData]     = useState({
    name: "", email: "", phone: "", how: "", message: "",
  });
  const [errors,      setErrors]      = useState({});
  const [submitError, setSubmitError] = useState("");

  const currentIntent = INTENTS.find((i) => i.id === activeIntent);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!formData.name.trim())  e.name = "Please enter your name";
    if (!formData.email.trim()) e.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      e.email = "Please enter a valid email address";
    if (!formData.message.trim()) e.message = "Please enter your message";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const saveToDb = async (payload) => {
    await fetch(`${API_URL}/api/contact`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        intent: payload.intent,
        name: payload.name,
        email: payload.email,
        phone: payload.phone,
        how: payload.how,
        message: payload.message,
      }),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!validate()) return;
    setIsLoading(true);
    try {
      // Save to Supabase first — always
      await saveToDb({ intent: currentIntent.label, ...formData });

      // Try EmailJS — ignore if it fails
      emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          to_name: "Admin",
          message: `Intent: ${currentIntent.label}\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nSource: ${formData.how}\nMessage: ${formData.message}`,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      ).catch(() => {});

      setSent(true);
      setFormData({ name: "", email: "", phone: "", how: "", message: "" });
    } catch {
      setSubmitError("Failed to submit. Please email us at connect@cograd.in");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us — CoGrad</title>
        <meta
          name="description"
          content="Get in touch with CoGrad for innovative and affordable education solutions. Discover our vision, services, and partnership opportunities."
        />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <Header />

      <main className="cu-page">

        {/* ── Hero ──────────────────────────────────────────────── */}
        <section className="cu-hero">
          <div className="cu-wrap">
            <motion.div
              className="cu-hero__inner"
              variants={FADE_UP}
              initial="hidden"
              animate="show"
            >
              <div className="cu-hero__shark">
                <span className="cu-hero__shark-dot" />
                🦈 As seen on <strong>Shark Tank India</strong>
              </div>
              <h1 className="cu-hero__h1">
                Have a question?<br /><em>Let's talk.</em>
              </h1>
              <p className="cu-hero__sub">
                Whether it's a partnership, a student query, or just saying hello — our team is here and happy to help.
              </p>
              <div className="cu-hero__ctas">
                <a href="#cu-form" className="cu-btn cu-btn--primary cu-btn--lg">
                  <Send size={16} />
                  Send a Message
                </a>
                <a
                  href={WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cu-btn cu-btn--outline cu-btn--lg"
                >
                  <WaIcon />
                  Chat on WhatsApp
                </a>
              </div>

              {/* ── Trust stats ── */}
              <div className="cu-hero__stats">
                <div className="cu-hero__stat">
                  <strong>{impact.schools.display}</strong>
                  <span>Schools Partnered</span>
                </div>
                <div className="cu-hero__stat-divider" />
                <div className="cu-hero__stat">
                  <strong>{impact.students.display}</strong>
                  <span>Students Impacted</span>
                </div>
                <div className="cu-hero__stat-divider" />
                <div className="cu-hero__stat">
                  <strong>24 hrs</strong>
                  <span>Avg. Response Time</span>
                </div>
              </div>
              <AsOfNote />
            </motion.div>
          </div>
        </section>


        {/* ── Body: Form + Sidebar ──────────────────────────────── */}
        <section className="cu-body">
          <div className="cu-wrap cu-body__grid">

            {/* Form card */}
            <motion.div
              id="cu-form"
              className="cu-form-card"
              variants={FADE_UP}
              custom={0}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            >
              {sent ? (
                <div className="cu-success">
                  <CheckCircle size={52} strokeWidth={1.6} />
                  <h3>Message sent!</h3>
                  <p>
                    Thanks for reaching out. Our team will get back to you
                    within 24 hours.
                  </p>
                  <button
                    className="cu-btn cu-btn--ghost"
                    onClick={() => setSent(false)}
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <>
                  {/* Intent selector */}
                  <div className="cu-intent">
                    <p className="cu-intent__lbl">I'm reaching out as a…</p>
                    <div className="cu-intent__pills">
                      {INTENTS.map(({ id, label, icon: Icon }) => (
                        <button
                          key={id}
                          type="button"
                          className={`cu-intent__pill${activeIntent === id ? " cu-intent__pill--on" : ""}`}
                          onClick={() => setActiveIntent(id)}
                        >
                          <Icon size={14} strokeWidth={2} />
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Dynamic form head */}
                  <div className="cu-form-card__head">
                    <h2>{currentIntent.heading}</h2>
                    <p>{currentIntent.sub}</p>
                  </div>

                  <form onSubmit={handleSubmit} noValidate className="cu-form">
                    <div className="cu-form__row cu-form__row--2">
                      <div className={`cu-field${errors.name ? " cu-field--err" : ""}`}>
                        <label className="cu-field__label">Full Name *</label>
                        <input
                          type="text" name="name" placeholder="e.g. Arjun Sharma"
                          value={formData.name} onChange={handleChange} disabled={isLoading}
                        />
                        {errors.name && <span className="cu-field__err">{errors.name}</span>}
                      </div>
                      <div className={`cu-field${errors.email ? " cu-field--err" : ""}`}>
                        <label className="cu-field__label">Email Address *</label>
                        <input
                          type="email" name="email" placeholder="you@example.com"
                          value={formData.email} onChange={handleChange} disabled={isLoading}
                        />
                        {errors.email && <span className="cu-field__err">{errors.email}</span>}
                      </div>
                    </div>

                    <div className="cu-form__row cu-form__row--2">
                      <div className="cu-field">
                        <label className="cu-field__label">Phone Number</label>
                        <input
                          type="tel" name="phone" placeholder="+91 98765 43210 (optional)"
                          value={formData.phone} onChange={handleChange} disabled={isLoading}
                        />
                      </div>
                      <div className="cu-field">
                        <label className="cu-field__label">How did you find us?</label>
                        <select
                          name="how" value={formData.how}
                          onChange={handleChange} disabled={isLoading}
                        >
                          <option value="">Select an option</option>
                          <option value="google">Google</option>
                          <option value="social">Social Media</option>
                          <option value="friend">From a Friend</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div className={`cu-field${errors.message ? " cu-field--err" : ""}`}>
                      <label className="cu-field__label">Your Message *</label>
                      <textarea
                        name="message"
                        placeholder={currentIntent.placeholder}
                        value={formData.message}
                        onChange={handleChange}
                        disabled={isLoading}
                        rows={5}
                      />
                      {errors.message && (
                        <span className="cu-field__err">{errors.message}</span>
                      )}
                    </div>

                    {submitError && (
                      <div className="cu-form__submit-err">{submitError}</div>
                    )}

                    <button
                      type="submit"
                      className={`cu-btn cu-btn--primary cu-btn--block${isLoading ? " cu-btn--loading" : ""}`}
                      disabled={isLoading}
                    >
                      {!isLoading && <Send size={16} />}
                      {isLoading ? "Sending…" : "Send Message"}
                    </button>
                  </form>
                </>
              )}
            </motion.div>

            {/* Sidebar */}
            <div className="cu-sidebar">
              {INFO_CARDS.map(({ icon: Icon, label, value, href, sub }, i) => (
                <motion.a
                  key={label}
                  href={href}
                  target={href.startsWith("http") ? "_blank" : undefined}
                  rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="cu-info-card"
                  variants={FADE_UP}
                  custom={i * 0.4}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                >
                  <div className="cu-info-card__icon"><Icon size={20} /></div>
                  <div className="cu-info-card__body">
                    <span className="cu-info-card__label">{label}</span>
                    <strong className="cu-info-card__value">{value}</strong>
                    <span className="cu-info-card__sub">{sub}</span>
                  </div>
                </motion.a>
              ))}

              {/* WhatsApp */}
              <motion.a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="cu-wa-btn"
                variants={FADE_UP}
                custom={1.4}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
              >
                <WaIcon />
                Chat on WhatsApp
              </motion.a>

              <div className="cu-cta-box">
                <Clock size={22} strokeWidth={1.8} />
                <h4>Quick Response Guaranteed</h4>
                <p>
                  Our team typically responds within 24 business hours. For
                  urgent queries, give us a call directly.
                </p>
              </div>
            </div>

          </div>
        </section>

        {/* ── Map ───────────────────────────────────────────────── */}
        <section className="cu-map-section">
          <div className="cu-wrap">
            <div className="cu-map-head">
              <span className="cu-label">Find Us</span>
              <h2 className="cu-map-head__h2">Visit our office</h2>
              <p className="cu-map-head__sub">
                PI Softek Ltd, C-56A/28, Sector 62, Noida 201301
              </p>
            </div>
            <div className="cu-map-card">
              <iframe
                src="https://maps.google.com/maps?q=PI+Softek+Ltd,+C-56A/28,+Sector+62,+Noida+201301&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="CoGrad Location"
              />
            </div>
          </div>
        </section>

      </main>

      {/* ── Floating WhatsApp (mobile) ─────────────────────────── */}
      <a
        href={WA_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className="cu-wa-float"
        aria-label="Chat on WhatsApp"
      >
        <WaIcon />
      </a>

      <Footer />
    </>
  );
};

export default ContactUs;
